"use client";

import { IconCoin } from "@tabler/icons-react";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { AddCreditsDialog } from "@/app/(app)/manage/billing/billing.overview";
import { Button } from "@/components/ui/button";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useOptionalWelcomeCreditReveal } from "@/components/welcome-credit-reveal";
import { useAuth } from "@/lib/auth";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useCreditTopUp } from "@/lib/billing/useCreditTopUp";
import { appSidebarNavFooter } from "@/lib/navigation/app-sidebar-nav";

const ADD_CREDITS_REVEAL_DELAY_MS = 500;

const slideInFromRight = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 12 },
  transition: { duration: 0.2, ease: [0, 0, 0.2, 1] },
} as const;

const formatUsdFromMicro = (micro: string | number | null | undefined) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(micro ?? 0) / 1_000_000);

const NavCreditsItem = () => {
  const { state, isMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;
  const { data, isPending, isError } = useBillingOverview();
  const topUp = useCreditTopUp();
  const overview = data?.billing_overview;
  const welcomeReveal = useOptionalWelcomeCreditReveal();
  const displayMicro = welcomeReveal
    ? welcomeReveal.displayBalanceMicro(overview?.credit_balance_micro)
    : Number(overview?.credit_balance_micro ?? 0);
  const balanceLabel = isError || (!isPending && overview == null) ? "—" : formatUsdFromMicro(displayMicro);
  const [revealAdd, setRevealAdd] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const revealTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showAdd = revealAdd || dialogOpen;

  useEffect(() => {
    return () => {
      if (revealTimeoutRef.current != null) {
        clearTimeout(revealTimeoutRef.current);
      }
    };
  }, []);

  const handlePointerEnter = () => {
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
    }
    revealTimeoutRef.current = setTimeout(() => {
      setRevealAdd(true);
      revealTimeoutRef.current = null;
    }, ADD_CREDITS_REVEAL_DELAY_MS);
  };

  const handlePointerLeave = () => {
    if (revealTimeoutRef.current != null) {
      clearTimeout(revealTimeoutRef.current);
      revealTimeoutRef.current = null;
    }
    setRevealAdd(false);
  };

  if (collapsed) {
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex size-8 items-center justify-center [&>span]:hidden">
              <IconCoin className="size-4 shrink-0" aria-hidden />
              <span>Credits {balanceLabel}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            Credits {balanceLabel}
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      {/* Non-interactive row — hover only reveals Add credits; not a control itself. */}
      {/* biome-ignore lint/a11y/useSemanticElements: hover-affordance row, not a form fieldset */}
      <div
        role="group"
        aria-label="Credits"
        className="flex h-8 w-full items-center gap-2 rounded-[2px] p-2 text-sm"
        onMouseEnter={handlePointerEnter}
        onMouseLeave={handlePointerLeave}
        onFocusCapture={() => setRevealAdd(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setRevealAdd(false);
          }
        }}
      >
        <IconCoin className="size-4 shrink-0" aria-hidden />
        <span className="truncate">Credits</span>
        <div className="relative ml-auto flex h-7 min-w-0 shrink-0 items-center justify-end overflow-visible">
          {isPending ? (
            <Skeleton className="h-4 w-12" />
          ) : (
            <>
              <span className={`text-muted-foreground ${showAdd ? "invisible" : ""}`}>{balanceLabel}</span>
              <AnimatePresence>
                {showAdd && overview != null ? (
                  <motion.div
                    key="add-credits"
                    className="absolute inset-y-0 right-0 flex items-center"
                    initial={slideInFromRight.initial}
                    animate={slideInFromRight.animate}
                    exit={slideInFromRight.exit}
                    transition={slideInFromRight.transition}
                  >
                    <AddCreditsDialog
                      topUp={topUp}
                      paymentMethods={overview.payment_methods}
                      invoices={overview.invoices}
                      monthlySpendLimitMicro={overview.monthly_spend_limit_micro}
                      currentSettings={overview.auto_recharge}
                      onOpenChange={setDialogOpen}
                      trigger={
                        <Button size="sm" className="h-7 px-2 text-xs">
                          Add credits
                        </Button>
                      }
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </>
          )}
        </div>
      </div>
    </SidebarMenuItem>
  );
};

/**
 * Utility links above the account menu: Docs + Credits.
 */
export const NavSidebarUtility = () => {
  const pathname = usePathname();
  const { docsSettings } = useAuth();
  const showDocs = docsSettings?.globally_enabled !== false;

  return (
    // Match main nav group padding (0.5rem 0.75rem) and menu gap (0.25rem / gap-1).
    <SidebarMenu className="px-3 py-2">
      {showDocs
        ? appSidebarNavFooter.map((item) => {
            const Icon = item.icon;
            const itemBase = item.url.replace(/\/$/, "");
            const isActive = pathname === item.url || pathname.startsWith(`${itemBase}/`);
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton asChild isActive={isActive} tooltip={item.title}>
                  <Link href={item.url}>
                    <Icon className="size-4" aria-hidden />
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })
        : null}
      <NavCreditsItem />
    </SidebarMenu>
  );
};
